import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Stethoscope,
  Clock,
  DollarSign,
  Activity,
  Filter,
  MoreVertical,
  Pill,
  Link2,
  Loader2,
  Download,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  Settings,
  Tag,
  Info,
  Calendar
} from 'lucide-react';
import { SchedulingRulesDialog } from '../components/services/SchedulingRulesDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceFormPanel = ({ service, isOpen, onClose, onSave, t }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: 'nursing',
    description: '',
    base_price: 0,
    duration_minutes: 60,
    requires_specialist_type: '',
    requires_medications: false,
    is_recurring: false,
    default_frequency: '',
    notes: '',
    status: 'active',
    alternative_names: []
  });
  const [saving, setSaving] = useState(false);
  const [newAltName, setNewAltName] = useState('');

  // Reset form when service changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: service?.name || '',
        code: service?.code || '',
        category: service?.category || 'nursing',
        description: service?.description || '',
        base_price: service?.base_price || 0,
        duration_minutes: service?.duration_minutes || 60,
        requires_specialist_type: service?.requires_specialist_type || '',
        requires_medications: service?.requires_medications || false,
        is_recurring: service?.is_recurring || false,
        default_frequency: service?.default_frequency || '',
        notes: service?.notes || '',
        status: service?.status || 'active',
        alternative_names: service?.alternative_names || []
      });
      setActiveTab('basic');
    }
  }, [service, isOpen]);

  const handleAddAltName = () => {
    const trimmed = newAltName.trim();
    if (trimmed && !formData.alternative_names.includes(trimmed)) {
      setFormData({
        ...formData,
        alternative_names: [...formData.alternative_names, trimmed]
      });
      setNewAltName('');
    }
  };

  const handleRemoveAltName = (nameToRemove) => {
    setFormData({
      ...formData,
      alternative_names: formData.alternative_names.filter(n => n !== nameToRemove)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAltName();
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error(t('form.requiredFields'));
      return;
    }

    setSaving(true);
    try {
      if (service?.id) {
        await axios.put(`${API_URL}/api/services/${service.id}`, formData);
        toast.success(t('messages.updated'));
      } else {
        await axios.post(`${API_URL}/api/services`, formData);
        toast.success(t('messages.created'));
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorCreating'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Stethoscope className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {service?.id ? t('form.editTitle') : t('form.title')}
              </SheetTitle>
              <SheetDescription>
                {service?.id ? t('form.editSubtitle') : t('form.subtitle')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('form.tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Settings className="h-4 w-4 mr-1" />
              {t('form.tabs.config')}
            </TabsTrigger>
            <TabsTrigger value="ocr" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Tag className="h-4 w-4 mr-1" />
              {t('form.tabs.ocr')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.name')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={t('form.namePlaceholder')}
                  />
                </div>
                <div>
                  <Label>{t('form.code')} *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder={t('form.codePlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.category')}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => setFormData({...formData, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home_hospitalization">{t('categories.home_hospitalization')}</SelectItem>
                      <SelectItem value="nursing">{t('categories.nursing')}</SelectItem>
                      <SelectItem value="physical_therapy">{t('categories.physical_therapy')}</SelectItem>
                      <SelectItem value="respiratory_therapy">{t('categories.respiratory_therapy')}</SelectItem>
                      <SelectItem value="occupational_therapy">{t('categories.occupational_therapy')}</SelectItem>
                      <SelectItem value="speech_therapy">{t('categories.speech_therapy')}</SelectItem>
                      <SelectItem value="medical_consultation">{t('categories.medical_consultation')}</SelectItem>
                      <SelectItem value="nutrition">{t('categories.nutrition')}</SelectItem>
                      <SelectItem value="psychology">{t('categories.psychology')}</SelectItem>
                      <SelectItem value="social_work">{t('categories.social_work')}</SelectItem>
                      <SelectItem value="wound_care">{t('categories.wound_care')}</SelectItem>
                      <SelectItem value="iv_therapy">{t('categories.iv_therapy')}</SelectItem>
                      <SelectItem value="palliative_care">{t('categories.palliative_care')}</SelectItem>
                      <SelectItem value="other">{t('categories.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('form.status')}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t('status.active')}</SelectItem>
                      <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                      <SelectItem value="discontinued">{t('status.discontinued')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t('form.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={t('form.descriptionPlaceholder')}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t('form.basePrice')}</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) => setFormData({...formData, base_price: parseFloat(e.target.value) || 0})}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('form.duration')}</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 0})}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('form.specialistType')}</Label>
                  <Input
                    value={formData.requires_specialist_type}
                    onChange={(e) => setFormData({...formData, requires_specialist_type: e.target.value})}
                    placeholder={t('form.specialistTypePlaceholder')}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Config Tab */}
            <TabsContent value="config" className="p-6 space-y-4 mt-0">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Pill className="h-5 w-5 text-[#E63946]" />
                    <div>
                      <p className="font-medium">{t('form.requiresMedications')}</p>
                      <p className="text-sm text-muted-foreground">{t('form.requiresMedicationsHelp')}</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.requires_medications}
                    onChange={(e) => setFormData({...formData, requires_medications: e.target.checked})}
                    className="h-5 w-5 rounded border-gray-300"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Activity className="h-5 w-5 text-[#E63946]" />
                    <div>
                      <p className="font-medium">{t('form.isRecurring')}</p>
                      <p className="text-sm text-muted-foreground">{t('form.isRecurringHelp')}</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                    className="h-5 w-5 rounded border-gray-300"
                  />
                </div>

                {formData.is_recurring && (
                  <div className="pl-4 border-l-2 border-[#E63946]/30">
                    <Label>{t('form.defaultFrequency')}</Label>
                    <Input
                      value={formData.default_frequency}
                      onChange={(e) => setFormData({...formData, default_frequency: e.target.value})}
                      placeholder={t('form.frequencyPlaceholder')}
                    />
                  </div>
                )}
              </div>

              <div>
                <Label>{t('form.notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={t('form.notesPlaceholder')}
                  rows={3}
                />
              </div>
            </TabsContent>

            {/* OCR Tab */}
            <TabsContent value="ocr" className="p-6 space-y-4 mt-0">
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <Label className="flex items-center gap-2 mb-2">
                  <Search className="h-4 w-4 text-blue-600" />
                  {t('form.alternativeNames')}
                </Label>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('form.alternativeNamesHelp')}
                </p>
                
                {/* Tags display */}
                {formData.alternative_names.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.alternative_names.map((name, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary" 
                        className="bg-blue-100 text-blue-800 hover:bg-blue-200 pr-1"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => handleRemoveAltName(name)}
                          className="ml-1 hover:text-red-600 p-0.5"
                        >
                          <XCircle className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Add new tag */}
                <div className="flex gap-2">
                  <Input
                    value={newAltName}
                    onChange={(e) => setNewAltName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('form.alternativeNamePlaceholder')}
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleAddAltName}
                    disabled={!newAltName.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('common:add')}
                  </Button>
                </div>
              </div>

              {formData.alternative_names.length === 0 && (
                <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                  <Tag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('form.noAlternativeNames')}</p>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common:cancel')}</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#E63946] hover:bg-[#d32836]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {service?.id ? t('form.update') : t('form.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Keep old ServiceForm for backward compatibility but mark as deprecated
const ServiceForm = ({ service, onSave, onCancel, t }) => {
  const [formData, setFormData] = useState({
    name: service?.name || '',
    code: service?.code || '',
    category: service?.category || 'nursing',
    description: service?.description || '',
    base_price: service?.base_price || 0,
    duration_minutes: service?.duration_minutes || 60,
    requires_specialist_type: service?.requires_specialist_type || '',
    requires_medications: service?.requires_medications || false,
    is_recurring: service?.is_recurring || false,
    default_frequency: service?.default_frequency || '',
    notes: service?.notes || '',
    status: service?.status || 'active',
    alternative_names: service?.alternative_names || []
  });
  const [saving, setSaving] = useState(false);
  const [newAltName, setNewAltName] = useState('');

  const handleAddAltName = () => {
    const trimmed = newAltName.trim();
    if (trimmed && !formData.alternative_names.includes(trimmed)) {
      setFormData({
        ...formData,
        alternative_names: [...formData.alternative_names, trimmed]
      });
      setNewAltName('');
    }
  };

  const handleRemoveAltName = (nameToRemove) => {
    setFormData({
      ...formData,
      alternative_names: formData.alternative_names.filter(n => n !== nameToRemove)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAltName();
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error(t('form.requiredFields'));
      return;
    }

    setSaving(true);
    try {
      if (service?.id) {
        await axios.put(`${API_URL}/api/services/${service.id}`, formData);
        toast.success(t('messages.updated'));
      } else {
        await axios.post(`${API_URL}/api/services`, formData);
        toast.success(t('messages.created'));
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorCreating'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.name')} *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder={t('form.namePlaceholder')}
          />
        </div>
        <div>
          <Label>{t('form.code')} *</Label>
          <Input
            value={formData.code}
            onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
            placeholder={t('form.codePlaceholder')}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('form.category')}</Label>
          <Select
            value={formData.category}
            onValueChange={(v) => setFormData({...formData, category: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="home_hospitalization">{t('categories.home_hospitalization')}</SelectItem>
              <SelectItem value="nursing">{t('categories.nursing')}</SelectItem>
              <SelectItem value="physical_therapy">{t('categories.physical_therapy')}</SelectItem>
              <SelectItem value="respiratory_therapy">{t('categories.respiratory_therapy')}</SelectItem>
              <SelectItem value="occupational_therapy">{t('categories.occupational_therapy')}</SelectItem>
              <SelectItem value="speech_therapy">{t('categories.speech_therapy')}</SelectItem>
              <SelectItem value="medical_consultation">{t('categories.medical_consultation')}</SelectItem>
              <SelectItem value="nutrition">{t('categories.nutrition')}</SelectItem>
              <SelectItem value="psychology">{t('categories.psychology')}</SelectItem>
              <SelectItem value="social_work">{t('categories.social_work')}</SelectItem>
              <SelectItem value="wound_care">{t('categories.wound_care')}</SelectItem>
              <SelectItem value="iv_therapy">{t('categories.iv_therapy')}</SelectItem>
              <SelectItem value="palliative_care">{t('categories.palliative_care')}</SelectItem>
              <SelectItem value="other">{t('categories.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('form.status')}</Label>
          <Select
            value={formData.status}
            onValueChange={(v) => setFormData({...formData, status: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('status.active')}</SelectItem>
              <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
              <SelectItem value="discontinued">{t('status.discontinued')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>{t('form.description')}</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder={t('form.descriptionPlaceholder')}
          rows={3}
        />
      </div>

      {/* Alternative Names for OCR/AI Matching */}
      <div className="border rounded-lg p-4 bg-blue-50/50">
        <Label className="flex items-center gap-2 mb-2">
          <Search className="h-4 w-4 text-blue-600" />
          {t('form.alternativeNames')}
        </Label>
        <p className="text-xs text-muted-foreground mb-3">
          {t('form.alternativeNamesHelp')}
        </p>
        
        {/* Tags display */}
        {formData.alternative_names.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {formData.alternative_names.map((name, idx) => (
              <Badge 
                key={idx} 
                variant="secondary" 
                className="bg-blue-100 text-blue-800 hover:bg-blue-200 pr-1"
              >
                {name}
                <button
                  type="button"
                  onClick={() => handleRemoveAltName(name)}
                  className="ml-1 hover:text-red-600 p-0.5"
                >
                  <XCircle className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        
        {/* Add new tag */}
        <div className="flex gap-2">
          <Input
            value={newAltName}
            onChange={(e) => setNewAltName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('form.alternativeNamePlaceholder')}
            className="flex-1"
          />
          <Button 
            type="button" 
            variant="outline" 
            size="sm"
            onClick={handleAddAltName}
            disabled={!newAltName.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>{t('form.basePrice')}</Label>
          <Input
            type="number"
            value={formData.base_price}
            onChange={(e) => setFormData({...formData, base_price: parseFloat(e.target.value) || 0})}
          />
        </div>
        <div>
          <Label>{t('form.duration')}</Label>
          <Input
            type="number"
            value={formData.duration_minutes}
            onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 0})}
          />
        </div>
        <div>
          <Label>{t('form.specialistType')}</Label>
          <Input
            value={formData.requires_specialist_type}
            onChange={(e) => setFormData({...formData, requires_specialist_type: e.target.value})}
            placeholder={t('form.specialistTypePlaceholder')}
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requires_medications}
            onChange={(e) => setFormData({...formData, requires_medications: e.target.checked})}
            className="rounded border-gray-300"
          />
          <span className="text-sm">{t('form.requiresMedications')}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_recurring}
            onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
            className="rounded border-gray-300"
          />
          <span className="text-sm">{t('form.isRecurring')}</span>
        </label>
      </div>

      {formData.is_recurring && (
        <div>
          <Label>{t('form.defaultFrequency')}</Label>
          <Input
            value={formData.default_frequency}
            onChange={(e) => setFormData({...formData, default_frequency: e.target.value})}
            placeholder={t('form.frequencyPlaceholder')}
          />
        </div>
      )}

      <div>
        <Label>{t('form.notes')}</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder={t('form.notesPlaceholder')}
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>{t('common:cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? t('common:saving') : service?.id ? t('form.update') : t('form.save')}
        </Button>
      </DialogFooter>
    </div>
  );
};

// ==================== SERVICE MEDICATIONS DIALOG ====================

const ServiceMedicationsDialog = ({ service, isOpen, onClose, onUpdate }) => {
  const [medications, setMedications] = useState([]);
  const [serviceMeds, setServiceMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && service) {
      fetchData();
    }
  }, [isOpen, service]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [medsRes, serviceMedsRes] = await Promise.all([
        axios.get(`${API_URL}/api/medications/catalog`),
        axios.get(`${API_URL}/api/services/${service.id}/medications`)
      ]);
      
      const medsData = Array.isArray(medsRes.data) ? medsRes.data : (medsRes.data?.data || []);
      // Map medication fields for display
      const mappedMeds = medsData.map(m => ({
        ...m,
        name: m.generic_name || m.commercial_name || 'Sin nombre'
      }));
      setMedications(mappedMeds);
      setServiceMeds(serviceMedsRes.data.medications || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedication = (medication) => {
    if (serviceMeds.some(m => m.medication_id === medication.id)) {
      toast.error('Este medicamento ya está agregado');
      return;
    }
    
    setServiceMeds([...serviceMeds, {
      medication_id: medication.id,
      quantity: 1,
      frequency: '',
      instructions: '',
      medication: medication
    }]);
  };

  const handleRemoveMedication = (medicationId) => {
    setServiceMeds(serviceMeds.filter(m => m.medication_id !== medicationId));
  };

  const handleUpdateMedication = (medicationId, field, value) => {
    setServiceMeds(serviceMeds.map(m => 
      m.medication_id === medicationId ? { ...m, [field]: value } : m
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const medsToSave = serviceMeds.map(m => ({
        medication_id: m.medication_id,
        quantity: m.quantity || 1,
        frequency: m.frequency || null,
        instructions: m.instructions || null
      }));
      
      await axios.put(`${API_URL}/api/services/${service.id}/medications`, medsToSave);
      toast.success('Medicamentos actualizados');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Error al guardar medicamentos');
    } finally {
      setSaving(false);
    }
  };

  const availableMedications = medications.filter(m => 
    !serviceMeds.some(sm => sm.medication_id === m.id) &&
    (m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     m.generic_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     m.commercial_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[#E63946]" />
            Medicamentos del Servicio
          </DialogTitle>
          <DialogDescription>
            {service?.name} - Configure los medicamentos que se agregarán automáticamente cuando se seleccione este servicio en una orden
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden grid grid-cols-2 gap-4">
            {/* Available Medications */}
            <div className="border rounded-lg p-3 flex flex-col">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Pill className="h-4 w-4" />
                Medicamentos Disponibles
              </h4>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar medicamento..."
                  className="pl-9 h-8 text-sm"
                />
              </div>
              <div className="flex-1 overflow-y-auto space-y-1 max-h-[300px]">
                {availableMedications.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {searchTerm ? 'Sin resultados' : 
                     medications.length === 0 ? 'No hay medicamentos registrados. Agregue medicamentos en el módulo de Medicamentos.' :
                     'Todos los medicamentos ya están agregados'}
                  </p>
                ) : (
                  availableMedications.slice(0, 50).map(med => (
                    <div
                      key={med.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleAddMedication(med)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{med.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {med.concentration} - {med.form}
                        </p>
                      </div>
                      <Plus className="h-4 w-4 text-green-600 flex-shrink-0" />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Selected Medications */}
            <div className="border rounded-lg p-3 flex flex-col">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-[#E63946]" />
                Medicamentos del Servicio ({serviceMeds.length})
              </h4>
              <div className="flex-1 overflow-y-auto space-y-2 max-h-[300px]">
                {serviceMeds.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Sin medicamentos configurados. Haga clic en un medicamento para agregarlo.
                  </p>
                ) : (
                  serviceMeds.map(sm => (
                    <div key={sm.medication_id} className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium">{sm.medication?.name || 'Medicamento'}</p>
                          <p className="text-xs text-gray-500">
                            {sm.medication?.concentration} - {sm.medication?.form}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRemoveMedication(sm.medication_id)}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Cantidad</Label>
                          <Input
                            type="number"
                            min="1"
                            value={sm.quantity}
                            onChange={(e) => handleUpdateMedication(sm.medication_id, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-7 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Frecuencia</Label>
                          <Input
                            value={sm.frequency || ''}
                            onChange={(e) => handleUpdateMedication(sm.medication_id, 'frequency', e.target.value)}
                            placeholder="Ej: c/8h"
                            className="h-7 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Instrucciones</Label>
                          <Input
                            value={sm.instructions || ''}
                            onChange={(e) => handleUpdateMedication(sm.medication_id, 'instructions', e.target.value)}
                            placeholder="Opcional"
                            className="h-7 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#d32836]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Guardar Medicamentos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ServicesPage = () => {
  const { t } = useTranslation('services');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [showMedsDialog, setShowMedsDialog] = useState(false);
  const [medsService, setMedsService] = useState(null);
  
  // Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Scheduling rules dialog state
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);
  const [schedulingService, setSchedulingService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, [searchTerm, categoryFilter, statusFilter]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/services`, {
        params: {
          search: searchTerm || undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setSelectedService(service);
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/services/${serviceToDelete.id}`);
      toast.success(t('messages.deleted'));
      setShowDeleteDialog(false);
      setServiceToDelete(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeleting'));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedService(null);
    fetchServices();
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'services_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('importExport.templateDownloaded'));
    } catch (error) {
      toast.error(t('importExport.errorDownloadingTemplate'));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/services/export/data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `services_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('importExport.exportSuccess', { count: services.length }));
    } catch (error) {
      toast.error(t('importExport.errorExporting'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error(t('importExport.onlyCsv'));
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResults(null);
    
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API_URL}/api/services/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(t('importExport.importSuccess', { count: response.data.imported }));
        fetchServices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('importExport.errorImporting'));
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Calculate stats
  const stats = {
    total: services.length,
    active: services.filter(s => s.status === 'active').length,
    inactive: services.filter(s => s.status === 'inactive').length
  };

  return (
    <div className="p-2 space-y-6" data-testid="services-page" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--ink)]" style={{ letterSpacing: '-0.02em' }}>{t('title')}</h1>
          <p className="text-[var(--ink-3)] mt-1 text-sm">{t('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
            className="border-[var(--line)] hover:bg-[var(--bg-soft)]"
          >
            <FileText className="h-4 w-4 mr-2" />
            {t('importExport.downloadTemplate')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting || services.length === 0}
            data-testid="export-btn"
            className="border-[var(--line)] hover:bg-[var(--bg-soft)]"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {t('importExport.export')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsImportOpen(true)}
            data-testid="import-btn"
            className="border-[var(--line)] hover:bg-[var(--bg-soft)]"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('importExport.import')}
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="new-service-btn" className="bg-[var(--red)] hover:bg-[var(--red-deep)] shadow-[var(--shadow-red)]">
            <Plus className="h-4 w-4 mr-2" />
            {t('newService')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[var(--ink-3)] uppercase tracking-wider font-medium">{t('common:total')} {t('title')}</p>
                <p className="text-2xl font-semibold text-[var(--ink)] mt-1">{stats.total}</p>
              </div>
              <div className="h-10 w-10 bg-[var(--red-soft)] rounded-lg flex items-center justify-center">
                <Stethoscope className="h-5 w-5 text-[var(--red)]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[var(--ink-3)] uppercase tracking-wider font-medium">{t('status.active')}</p>
                <p className="text-2xl font-semibold text-green-600 mt-1">{stats.active}</p>
              </div>
              <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('status.inactive')}</p>
                <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
              </div>
              <Activity className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('searchPlaceholder')}
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('form.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:all')} {t('form.category')}</SelectItem>
                <SelectItem value="home_hospitalization">{t('categories.home_hospitalization')}</SelectItem>
                <SelectItem value="nursing">{t('categories.nursing')}</SelectItem>
                <SelectItem value="physical_therapy">{t('categories.physical_therapy')}</SelectItem>
                <SelectItem value="respiratory_therapy">{t('categories.respiratory_therapy')}</SelectItem>
                <SelectItem value="occupational_therapy">{t('categories.occupational_therapy')}</SelectItem>
                <SelectItem value="speech_therapy">{t('categories.speech_therapy')}</SelectItem>
                <SelectItem value="medical_consultation">{t('categories.medical_consultation')}</SelectItem>
                <SelectItem value="nutrition">{t('categories.nutrition')}</SelectItem>
                <SelectItem value="psychology">{t('categories.psychology')}</SelectItem>
                <SelectItem value="social_work">{t('categories.social_work')}</SelectItem>
                <SelectItem value="wound_care">{t('categories.wound_care')}</SelectItem>
                <SelectItem value="iv_therapy">{t('categories.iv_therapy')}</SelectItem>
                <SelectItem value="palliative_care">{t('categories.palliative_care')}</SelectItem>
                <SelectItem value="other">{t('categories.other')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder={t('common:status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:all')}</SelectItem>
                <SelectItem value="active">{t('status.active')}</SelectItem>
                <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Services List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
            </div>
          ) : services.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.service')}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.code')}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.category')}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.price')}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.duration')}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.status')}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map(service => (
                    <tr key={service.id} className="border-t hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-[#FDE8EA] dark:bg-[#E63946]/20 flex items-center justify-center">
                            <Stethoscope className="h-5 w-5 text-[#E63946]" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{service.name}</p>
                            {service.description && (
                              <p className="text-xs text-muted-foreground line-clamp-1">{service.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{service.code}</Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {t(`categories.${service.category}`, service.category)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-medium">${service.base_price?.toLocaleString()}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-sm">
                        {service.duration_minutes ? `${service.duration_minutes} min` : '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={
                          service.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                          service.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        }>
                          {t(`status.${service.status}`)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSchedulingService(service);
                              setShowSchedulingDialog(true);
                            }}
                            title="Reglas de Programación"
                            data-testid={`service-scheduling-btn-${service.id}`}
                          >
                            <Calendar className="h-4 w-4 text-purple-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setMedsService(service);
                              setShowMedsDialog(true);
                            }}
                            title={t('medications.title')}
                          >
                            <Pill className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(service)}
                            title={t('common:edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setServiceToDelete(service);
                              setShowDeleteDialog(true);
                            }}
                            title={t('common:delete')}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Stethoscope className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('table.noServices')}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('newService')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Form Panel (Right Side Sheet) */}
      <ServiceFormPanel
        service={selectedService}
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedService(null);
        }}
        onSave={() => {
          fetchServices();
          setSelectedService(null);
        }}
        t={t}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common:confirmDelete')}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {t('confirmDelete')} &ldquo;{serviceToDelete?.name}&rdquo;
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common:cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {t('common:delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medications Dialog */}
      <ServiceMedicationsDialog
        service={medsService}
        isOpen={showMedsDialog}
        onClose={() => {
          setShowMedsDialog(false);
          setMedsService(null);
        }}
        onUpdate={fetchServices}
      />

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#E63946]" />
              {t('importExport.importTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('importExport.importSubtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Input */}
            <div>
              <Label>{t('importExport.selectFile')}</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="mt-1"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground mt-1">
                  {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  {t('importExport.results.title')}
                </h4>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-600">{importResults.imported}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.results.imported')}</p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.results.skipped')}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                    <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-600">{importResults.errors?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.results.errors')}</p>
                  </div>
                </div>

                {/* Error Details */}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-sm">
                    {importResults.errors.map((err, idx) => (
                      <p key={`error-${idx}`} className="text-red-600 text-xs py-1 border-b last:border-0">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog}>
              {t('common:close')}
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importFile || isImporting}
              className="bg-[#E63946] hover:bg-[#d32836]"
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {t('importExport.importBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheduling Rules Dialog */}
      <SchedulingRulesDialog
        open={showSchedulingDialog}
        onOpenChange={setShowSchedulingDialog}
        service={schedulingService}
        onSave={fetchServices}
      />
    </div>
  );
};

export default ServicesPage;
